<template>
  <v-dialog v-model="visible" persistent max-width="700" scrollable @click:outside="onEdit">
    <v-card>
      <v-toolbar color="primary" dark flat>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          {{ $t('memberDataSection.greenCoffee.reviewSubmission') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="onEdit">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-0" style="max-height: calc(90vh - 120px); overflow-y: auto;">
        <!-- Summary Row -->
        <div class="px-6 pt-5 pb-4">
          <v-row>
            <v-col cols="12" sm="4">
              <div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
              <div class="field-value">{{ summary.isManualLotId ? (summary.outturnNumber || '—') : '—' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalQuantity') }}</div>
              <div class="field-value">{{ summary.totalGreenBeans != null ? summary.totalGreenBeans + ' kg' : '—' }}</div>
            </v-col>
            <v-col cols="12" sm="4">
              <div class="field-label">{{ $t('memberDataSection.greenCoffee.startDate') }}</div>
              <div class="field-value">{{ summary.processDate || '—' }}</div>
            </v-col>
          </v-row>
        </div>

        <v-divider></v-divider>

        <!-- Parchment Information -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('parchment')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.parchmentInformation') }}</span>
          <v-icon>{{ sections.parchment ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.parchment">
            <template v-if="summary.lotId || summary.processingBatchDisplay">
              <div class="px-6 py-4">
                <v-row>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchId') }}</div>
                    <div class="field-value">{{ summary.lotId || summary.processingBatchDisplay || '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                    <div class="field-value">{{ summary.quantity != null && summary.quantity !== '' ? summary.quantity + ' kg' : '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.supplier') }}</div>
                    <div class="field-value">{{ summary.supplier || '—' }}</div>
                  </v-col>
                  <v-col v-if="summary.typeInfo" cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.species') }}</div>
                    <div class="field-value">{{ summary.typeInfo }}</div>
                  </v-col>
                  <v-col v-if="summary.processInfo" cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.process') }}</div>
                    <div class="field-value">{{ summary.processInfo }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalCoffeeCherryQty') }}</div>
                    <div class="field-value">{{ summary.totalInputQuantity != null && summary.totalInputQuantity !== '' ? summary.totalInputQuantity + ' kg' : '—' }}</div>
                  </v-col>
                </v-row>
              </div>
            </template>
            <div v-else class="px-6 py-4 text--secondary text-body-2">{{ $t('memberDataSection.greenCoffee.noParchmentBatches') }}</div>
          </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <!-- Process Information -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('process')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.processInformation') }}</span>
          <v-icon>{{ sections.process ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.process" class="px-6 py-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.airHumidity') }}</div>
                <div class="field-value">{{ summary.airHumidity != null && summary.airHumidity !== '' ? summary.airHumidity : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.density') }}</div>
                <div class="field-value">{{ summary.density != null && summary.density !== '' ? summary.density + ' kg/cm3' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.batchProductionDensity') }}</div>
                <div class="field-value">{{ summary.batchProductionDensity != null && summary.batchProductionDensity !== '' ? summary.batchProductionDensity + ' kg/cm3' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                <div class="field-value">{{ summary.qualityScoreName || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.weightBeforeProcessing') }}</div>
                <div class="field-value">{{ summary.totalInputQuantity != null && summary.totalInputQuantity !== '' ? summary.totalInputQuantity + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentagePrimaryDefect') }}</div>
                <div class="field-value">{{ summary.percentagePrimaryDefect != null && summary.percentagePrimaryDefect !== '' ? summary.percentagePrimaryDefect + '%' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentageSecondaryDefect') }}</div>
                <div class="field-value">{{ summary.percentageSecondaryDefect != null && summary.percentageSecondaryDefect !== '' ? summary.percentageSecondaryDefect + '%' : '—' }}</div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <!-- Green Beans Information -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('greenBeans')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.greenBeansInformation') }}</span>
          <v-icon>{{ sections.greenBeans ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.greenBeans" class="px-6 py-4">
            <v-row>
              <v-col v-if="summary.isManualLotId" cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.outturnNumber') }}</div>
                <div class="field-value">{{ summary.outturnNumber || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalGreenBeans') }}</div>
                <div class="field-value">{{ summary.totalGreenBeans != null ? summary.totalGreenBeans + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.numberOfBags') }}</div>
                <div class="field-value">{{ summary.totalBags != null ? summary.totalBags : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                <div class="field-value">{{ summary.leftoverQuantity != null ? summary.leftoverQuantity + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.expiryDate') }}</div>
                <div class="field-value">{{ summary.expiryDate || '—' }}</div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <!-- Traceability Label -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('traceability')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.traceabilityLabel') }}</span>
          <v-icon>{{ sections.traceability ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.traceability" class="px-6 py-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                <div class="field-value">{{ summary.labelTitle || '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.selectProductType') }}</div>
                <div class="field-value">{{ summary.productTypeName || '—' }}</div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <!-- Quality Control -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('quality')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.qualityControl') }}</span>
          <v-icon>{{ sections.quality ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.quality">
            <!-- Pie chart -->
            <div v-if="pieSegments.length" class="d-flex align-center px-6 pt-4 pb-2">
              <svg width="220" height="220" viewBox="0 0 220 220" class="flex-shrink-0">
                <path v-for="seg in pieSegments" :key="'pie-' + seg.lotId" :d="seg.path" :fill="seg.color" />
              </svg>
              <div class="ml-6">
                <div v-for="seg in chartSegments" :key="'legend-' + seg.lotId" class="d-flex align-center mb-2">
                  <span class="legend-dot mr-2" :style="{ backgroundColor: seg.color }"></span>
                  <span class="text-body-2">{{ seg.lotId }}: {{ seg.percentage }}%</span>
                </div>
              </div>
            </div>

            <!-- Lot cards -->
            <div v-for="(lot, idx) in summary.qualityGradingLots" :key="'lot-' + idx">
              <v-divider></v-divider>
              <div class="px-6 py-4">
                <div class="lot-id mb-3">{{ $t('memberDataSection.greenCoffee.lotId') }}: {{ lot.lotId || '—' }}</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.quantity') }}</div>
                    <div class="field-value">{{ lot.quantity != null ? lot.quantity + ' kg' : '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.packing') }}</div>
                    <div class="field-value">{{ lot.unitSize != null ? lot.unitSize + ' kg Bag' : '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalBags') }}</div>
                    <div class="field-value">{{ lot.totalBags != null ? lot.totalBags : '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.leftoverQuantity') }}</div>
                    <div class="field-value">{{ lot.leftoverQuantity != null ? lot.leftoverQuantity + ' kg' : '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.qualityScore') }}</div>
                    <div class="field-value">{{ lot.qualityScore || '—' }}</div>
                  </v-col>
                  <v-col cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.label') }}</div>
                    <div class="field-value">{{ displayLotLabel(lot.label) }}</div>
                  </v-col>
                  <v-col v-if="lot.percentage != null" cols="12" sm="4">
                    <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                    <div class="field-value">{{ lot.percentage }}%</div>
                  </v-col>
                </v-row>
              </div>
            </div>
            <div v-if="!summary.qualityGradingLots || !summary.qualityGradingLots.length" class="px-6 py-4 text--secondary text-body-2">
              {{ $t('memberDataSection.greenCoffee.noQualityGrades') }}
            </div>
          </div>
        </v-expand-transition>

        <v-divider></v-divider>

        <!-- Waste -->
        <div class="section-header d-flex justify-space-between align-center px-6 py-3" @click="toggleSection('waste')">
          <span class="section-title">{{ $t('memberDataSection.greenCoffee.waste') }}</span>
          <v-icon>{{ sections.waste ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </div>
        <v-expand-transition>
          <div v-if="sections.waste" class="px-6 py-4">
            <v-row>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.totalWaste') }}</div>
                <div class="field-value">{{ totalWaste != null ? totalWaste + ' kg' : '—' }}</div>
              </v-col>
              <v-col cols="12" sm="4">
                <div class="field-label">{{ $t('memberDataSection.greenCoffee.percentage') }}</div>
                <div class="field-value">{{ wastePercentage }}</div>
              </v-col>
            </v-row>
          </div>
        </v-expand-transition>
      </v-card-text>

      <v-divider></v-divider>
      <v-card-actions class="pa-4">
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" @click="onEdit">{{ $t('edit') }}</v-btn>
        <v-btn color="primary" class="white--text ml-2" @click="onContinue">{{ $t('continue') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
const PIE_COLORS = ["#4caf50", "#ff9800", "#607d8b", "#2196f3", "#9c27b0", "#f44336", "#00bcd4"];

export default {
  name: "GreenCoffeeReviewModal",
  props: {
    value: { type: Boolean, default: false },
    summary: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      visible: this.value,
      sections: {
        parchment: true,
        process: true,
        greenBeans: true,
        traceability: true,
        quality: true,
        waste: true,
      },
    };
  },
  computed: {
    totalWaste() {
      const total = Number(this.summary.totalGreenBeans) || 0;
      if (!total) return null;
      const lotsSum = (this.summary.qualityGradingLots || []).reduce(
        (sum, lot) => sum + (Number(lot.quantity) || 0),
        0
      );
      return Math.max(0, total - lotsSum);
    },
    wastePercentage() {
      const waste = this.totalWaste;
      if (waste == null) return "—";
      const total = Number(this.summary.totalGreenBeans) || 0;
      if (!total) return "—";
      return Math.round((waste / total) * 100) + " %";
    },
    chartSegments() {
      const total = Number(this.summary.totalGreenBeans) || 0;
      const lots = this.summary.qualityGradingLots || [];
      const segments = [];
      lots.forEach((lot, i) => {
        const qty = Number(lot.quantity) || 0;
        const pct = total ? Math.round((qty / total) * 100) : 0;
        if (pct > 0) {
          segments.push({
            lotId: lot.lotId || `Lot ${i + 1}`,
            percentage: pct,
            color: PIE_COLORS[i % PIE_COLORS.length],
          });
        }
      });
      const waste = Number(this.totalWaste) || 0;
      const wastePct = total ? Math.round((waste / total) * 100) : 0;
      if (wastePct > 0) segments.push({ lotId: "Waste", percentage: wastePct, color: "#bdbdbd" });
      const used = segments.reduce((s, seg) => s + seg.percentage, 0);
      if (used < 100) segments.push({ lotId: "Other", percentage: 100 - used, color: "#e0e0e0" });
      return segments;
    },
    pieSegments() {
      const segments = this.chartSegments.filter((s) => s.percentage > 0);
      if (!segments.length) return [];
      const cx = 110, cy = 110, r = 95;
      const paths = [];
      let currentAngle = -90;
      for (const seg of segments) {
        const sweep = (seg.percentage / 100) * 360;
        paths.push({ ...seg, path: this.describeSlice(cx, cy, r, currentAngle, currentAngle + sweep) });
        currentAngle += sweep;
      }
      return paths;
    },
  },
  watch: {
    value(v) {
      this.visible = v;
    },
    visible(v) {
      this.$emit("input", v);
    },
  },
  methods: {
    toggleSection(key) {
      this.sections[key] = !this.sections[key];
    },
    displayLotLabel(label) {
      if (label == null) return "—";
      if (typeof label === "object") return label.title || (label.id != null ? String(label.id) : "—");
      if (typeof label === "string") {
        try {
          const p = JSON.parse(label);
          return (p && p.title) || (p && p.id != null ? String(p.id) : label) || "—";
        } catch (_) {
          return label || "—";
        }
      }
      return "—";
    },
    describeSlice(cx, cy, r, startDeg, endDeg) {
      const sweepDeg = endDeg - startDeg;
      if (sweepDeg >= 360) {
        return `M ${cx - r} ${cy} a ${r} ${r} 0 1 0 ${2 * r} 0 a ${r} ${r} 0 1 0 ${-2 * r} 0`;
      }
      const toRad = (deg) => (deg * Math.PI) / 180;
      const x1 = cx + r * Math.cos(toRad(startDeg));
      const y1 = cy + r * Math.sin(toRad(startDeg));
      const x2 = cx + r * Math.cos(toRad(endDeg));
      const y2 = cy + r * Math.sin(toRad(endDeg));
      const large = endDeg - startDeg > 180 ? 1 : 0;
      return `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    },
    onEdit() {
      this.visible = false;
      this.$emit("edit");
    },
    onContinue() {
      this.visible = false;
      this.$emit("continue");
    },
  },
};
</script>

<style scoped>
.section-header {
  cursor: pointer;
  user-select: none;
  background-color: #fafafa;
}
.section-header:hover {
  background-color: #f5f5f5;
}
.section-title {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.lot-id {
  color: #6d4c41;
  font-weight: 700;
  font-size: 15px;
}
.field-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.54);
  line-height: 1.4;
}
.field-value {
  font-weight: 700;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 2px;
}
.legend-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}
</style>
